import { create } from 'zustand';

export const LHS_REFRESH_INTERVAL_MS = 30 * 60 * 1000;
export const LHS_OVERRIDE_STALE_MS = 10 * 60 * 1000;
export const LHS_STALE_CHECK_INTERVAL_MS = 60 * 1000;

export const LHS_MEETING_SECTIONS = {
    OUTRIGHT: 'meetingDatesOutright',
    SPREAD: 'meetingDatesSpread',
    ALL: 'all'
};

export const LHS_TENOR_SECTIONS = {
    OUTRIGHT: 'outrightTenors',
    SPREAD: 'spreadTenors',
    BUTTERFLY: 'butterflyTenors',
    SIXS3S_OUTRIGHT: 'sixs3sOutrightTenors',
    BOB_OUTRIGHT: 'bobOutrightTenors',
    ALL: 'all'
};

const LHS_DEFAULT_TENOR_TAB = '_default';

const mergeCurrenciesFromResponse = (data, currency) => (
    currency ? [currency] : Object.keys(data ?? {})
);

export const mergeLhsMeetingData = (prev, response) => {
    const { section, currency, data } = response ?? {};
    if (!data) return prev ?? {};

    if (!currency && (!section || section === LHS_MEETING_SECTIONS.ALL)) {
        return data;
    }

    const next = { ...(prev ?? {}) };
    mergeCurrenciesFromResponse(data, currency).forEach((c) => {
        if (!data[c]) return;
        if (!section || section === LHS_MEETING_SECTIONS.ALL) {
            next[c] = { ...next[c], ...data[c] };
            return;
        }
        next[c] = {
            ...next[c],
            [section]: data[c][section]
        };
    });
    return next;
};

export const mergeLhsTenorData = (prev, response) => {
    const { section, currency, data } = response ?? {};
    if (!data) return prev ?? {};

    if (!currency && (!section || section === LHS_TENOR_SECTIONS.ALL)) {
        return data;
    }

    const next = { ...(prev ?? {}) };
    mergeCurrenciesFromResponse(data, currency).forEach((c) => {
        if (!data[c]) return;
        const { lastUpdated: incomingLastUpdated, ...incomingRest } = data[c];
        next[c] = {
            ...next[c],
            ...incomingRest,
            lastUpdated: {
                ...next[c]?.lastUpdated,
                ...(incomingLastUpdated ?? {})
            }
        };
    });
    return next;
};

export const hasLhsCurrencyData = (currency, meetingData, tenorData) => (
    Boolean(meetingData?.[currency]?.meetingDatesOutright)
    && Boolean(tenorData?.[currency]?.outrightTenors)
);

export const getLhsTenorTabKey = (isMeetingDates, activeTenorTab) => (
    isMeetingDates ? LHS_DEFAULT_TENOR_TAB : (activeTenorTab ?? 'spot')
);

export const getLhsAudLegTenors = (item, sectionId) => {
    if (!item?.tenor) return [];
    if (sectionId === LHS_TENOR_SECTIONS.SPREAD || sectionId === LHS_TENOR_SECTIONS.BUTTERFLY) {
        return item.tenor.split(' x ').map((leg) => leg.trim());
    }
    return [item.tenor.trim()];
};

export const getLhsItemKey = ({
    currency,
    sectionId,
    tenorTab = LHS_DEFAULT_TENOR_TAB,
    item
}) => {
    if (!item || !currency || !sectionId) return '';

    if (item.isMeetingSpread) {
        return `${currency}|${sectionId}|${tenorTab}|ms|${item.label1}|${item.label2}`;
    }

    if (item.tenor && (item.irsId || item.curveName)) {
        const irsKey = item.irsId ?? item.curveName;
        return `${currency}|${sectionId}|${tenorTab}|${item.tenor}|${irsKey}`;
    }

    const label = item.displayLabel ?? item.label ?? item.id ?? '';
    const variant = item.variant ?? '';
    const id = item.id ?? label;
    return `${currency}|${sectionId}|${tenorTab}|md|${id}|${variant}`;
};

export const getLhsItemLabel = (item) => {
    if (item.isMeetingSpread) return `${item.label1} → ${item.label2}`;
    return item.displayLabel ?? item.label ?? item.tenor ?? '';
};

export const enrichLhsItems = ({
    items,
    currency,
    sectionId,
    tenorTab,
    priceOverrides
}) => {
    const list = Array.isArray(items) ? items : [];
    return list.map((item) => {
        const itemKey = getLhsItemKey({
            currency, sectionId, tenorTab, item
        });
        const deonMid = item.midPrice ?? null;
        const overrideValue = priceOverrides[itemKey]?.value;
        const hasManual = overrideValue != null && overrideValue !== '';
        const hasPrice = hasManual || (deonMid != null && deonMid !== '');
        return {
            ...item,
            itemKey,
            deonMid,
            displayPrice: hasManual ? overrideValue : deonMid,
            priceSource: hasManual ? 'manual' : 'deon',
            hasPrice
        };
    });
};

const TENOR_SECTION_KEYS = [
    'outrightTenors',
    'spreadTenors',
    'butterflyTenors',
    'sixs3sOutrightTenors',
    'bobOutrightTenors'
];

const collectIrsIdsFromTenorSections = (source, target) => {
    if (!source) return;
    TENOR_SECTION_KEYS.forEach((key) => {
        const bucket = source[key];
        if (!bucket) return;
        Object.values(bucket).forEach((rows) => {
            if (!Array.isArray(rows)) return;
            rows.forEach((row) => {
                if (row?.irsId) target.add(row.irsId);
            });
        });
    });
};

const collectIrsSecurityIdsFromTenorSections = (source, target) => {
    if (!source) return;
    TENOR_SECTION_KEYS.forEach((key) => {
        const bucket = source[key];
        if (!bucket) return;
        Object.values(bucket).forEach((rows) => {
            if (!Array.isArray(rows)) return;
            rows.forEach((row) => {
                if (row?.irsSecurityId) target.add(row.irsSecurityId);
                if (Array.isArray(row?.legSecurityIds)) {
                    row.legSecurityIds.forEach((legId) => {
                        if (legId) target.add(legId);
                    });
                }
            });
        });
    });
};

export const getLhsIrsIdsForCurrency = (meetingData, tenorData, currency) => {
    const ids = new Set();
    if (!currency) return ids;
    const meeting = meetingData?.[currency];
    if (meeting?.meetingDatesOutright?.irsId) ids.add(meeting.meetingDatesOutright.irsId);
    if (meeting?.meetingDatesSpread?.irsId) ids.add(meeting.meetingDatesSpread.irsId);
    const tenor = tenorData?.[currency];
    collectIrsIdsFromTenorSections(tenor, ids);
    if (Array.isArray(tenor?.curves)) {
        tenor.curves.forEach((curve) => collectIrsIdsFromTenorSections(curve, ids));
    }
    return ids;
};

export const getLhsIrsSecurityIdsForCurrency = (meetingData, tenorData, currency) => {
    const ids = new Set();
    if (!currency) return ids;
    const tenor = tenorData?.[currency];
    collectIrsSecurityIdsFromTenorSections(tenor, ids);
    if (Array.isArray(tenor?.curves)) {
        tenor.curves.forEach((curve) => collectIrsSecurityIdsFromTenorSections(curve, ids));
    }
    return ids;
};

const patchRowsForSection = (rows, midsBySecurityId) => {
    if (!Array.isArray(rows) || rows.length === 0) return rows;
    let changed = false;
    const nextRows = rows.map((row) => {
        const secId = row?.irsSecurityId;
        if (!secId || !midsBySecurityId.has(secId)) return row;
        const nextMid = midsBySecurityId.get(secId);
        if (nextMid == null || nextMid === row.midPrice) return row;
        changed = true;
        return { ...row, midPrice: nextMid };
    });
    return changed ? nextRows : rows;
};

const patchTenorSectionBuckets = (source, midsBySecurityId) => {
    if (!source) return source;
    let scopeChanged = false;
    const nextSource = { ...source };
    TENOR_SECTION_KEYS.forEach((key) => {
        const bucket = source[key];
        if (!bucket || typeof bucket !== 'object' || Array.isArray(bucket)) return;
        let bucketChanged = false;
        const nextBucket = { ...bucket };
        Object.entries(bucket).forEach(([startKey, rows]) => {
            const nextRows = patchRowsForSection(rows, midsBySecurityId);
            if (nextRows !== rows) {
                nextBucket[startKey] = nextRows;
                bucketChanged = true;
            }
        });
        if (bucketChanged) {
            nextSource[key] = nextBucket;
            scopeChanged = true;
        }
    });
    return scopeChanged ? nextSource : source;
};

export const applyLhsBgcMidUpdates = (tenorData, midsBySecurityId) => {
    if (!tenorData || !midsBySecurityId || midsBySecurityId.size === 0) return tenorData;
    let anyChanged = false;
    const next = { ...tenorData };
    Object.entries(tenorData).forEach(([currency, currencyData]) => {
        if (!currencyData || typeof currencyData !== 'object') return;
        const patchedFlat = patchTenorSectionBuckets(currencyData, midsBySecurityId);
        let patchedCurves = currencyData.curves;
        if (Array.isArray(currencyData.curves)) {
            let curvesChanged = false;
            const nextCurves = currencyData.curves.map((curve) => {
                const patchedCurve = patchTenorSectionBuckets(curve, midsBySecurityId);
                if (patchedCurve !== curve) curvesChanged = true;
                return patchedCurve;
            });
            if (curvesChanged) {
                patchedCurves = nextCurves;
            }
        }
        if (patchedFlat !== currencyData || patchedCurves !== currencyData.curves) {
            next[currency] = { ...patchedFlat, ...(Array.isArray(patchedCurves) ? { curves: patchedCurves } : {}) };
            anyChanged = true;
        }
    });
    return anyChanged ? next : tenorData;
};

export const hasStaleOverrideForCurrency = (
    priceOverrides,
    currency,
    thresholdMs = LHS_OVERRIDE_STALE_MS,
    now = Date.now()
) => {
    if (!priceOverrides || !currency) return false;
    const prefix = `${currency}|`;
    const cutoff = now - thresholdMs;
    return Object.entries(priceOverrides).some(
        ([key, override]) => key.startsWith(prefix)
            && typeof override?.editedAt === 'number'
            && override.editedAt < cutoff
    );
};

export const countLhsCurrencySelections = (selections, currency) => {
    const sections = selections[currency] ?? {};
    return Object.values(sections).reduce(
        (total, section) => total + Object.values(section).reduce(
            (sectionTotal, tabSel) => sectionTotal + Object.values(tabSel).filter(Boolean).length,
            0
        ),
        0
    );
};

const emptyCurrencyState = () => ({ AUD: {}, NZD: {} });

const useLhsMatchingStore = create((set) => ({
    selections: emptyCurrencyState(),
    priceOverrides: {},
    activeTenorTabs: emptyCurrencyState(),

    setActiveTenorTab: (currency, sectionId, tabKey) => set((state) => ({
        activeTenorTabs: {
            ...state.activeTenorTabs,
            [currency]: {
                ...state.activeTenorTabs[currency],
                [sectionId]: tabKey
            }
        }
    })),

    toggleSelection: (currency, sectionId, tenorTab, itemKey) => set((state) => {
        const currencySel = state.selections[currency] ?? {};
        const sectionSel = currencySel[sectionId] ?? {};
        const tabSel = { ...(sectionSel[tenorTab] ?? {}) };
        if (tabSel[itemKey]) delete tabSel[itemKey];
        else tabSel[itemKey] = true;
        return {
            selections: {
                ...state.selections,
                [currency]: {
                    ...currencySel,
                    [sectionId]: {
                        ...sectionSel,
                        [tenorTab]: tabSel
                    }
                }
            }
        };
    }),

    selectAll: (currency, sectionId, tenorTab, itemKeys, shouldSelect) => set((state) => {
        const currencySel = state.selections[currency] ?? {};
        const sectionSel = currencySel[sectionId] ?? {};
        const tabSel = shouldSelect
            ? itemKeys.reduce((acc, key) => ({ ...acc, [key]: true }), {})
            : {};
        return {
            selections: {
                ...state.selections,
                [currency]: {
                    ...currencySel,
                    [sectionId]: {
                        ...sectionSel,
                        [tenorTab]: tabSel
                    }
                }
            }
        };
    }),

    setPriceOverride: (itemKey, value) => set((state) => ({
        priceOverrides: {
            ...state.priceOverrides,
            [itemKey]: { value, editedAt: Date.now() }
        }
    })),

    clearPriceOverride: (itemKey) => set((state) => {
        if (!(itemKey in state.priceOverrides)) return state;
        const next = { ...state.priceOverrides };
        delete next[itemKey];
        return { priceOverrides: next };
    }),

    clearPriceOverridesForKeys: (itemKeys) => set((state) => {
        if (!itemKeys?.length) return state;
        const next = { ...state.priceOverrides };
        let changed = false;
        itemKeys.forEach((key) => {
            if (key in next) {
                delete next[key];
                changed = true;
            }
        });
        return changed ? { priceOverrides: next } : state;
    }),

    clearPriceOverridesForRefresh: (currency, sectionId = null) => set((state) => {
        const prefix = `${currency}|`;
        const sectionNeedle = sectionId ? `|${sectionId}|` : null;
        const next = { ...state.priceOverrides };
        Object.keys(next).forEach((key) => {
            if (!key.startsWith(prefix)) return;
            if (sectionNeedle && !key.includes(sectionNeedle)) return;
            delete next[key];
        });
        return { priceOverrides: next };
    }),

    clearTabSelections: (currency, sectionId, tenorTab) => set((state) => {
        const currencySel = state.selections[currency] ?? {};
        const sectionSel = currencySel[sectionId] ?? {};
        return {
            selections: {
                ...state.selections,
                [currency]: {
                    ...currencySel,
                    [sectionId]: {
                        ...sectionSel,
                        [tenorTab]: {}
                    }
                }
            }
        };
    }),

    clearCurrencySelections: (currency) => set((state) => ({
        selections: {
            ...state.selections,
            [currency]: {}
        }
    }))
}));

export default useLhsMatchingStore;
export { useLhsMatchingStore };
